import {
  clinicianMaxUpgradeModalEntryPointAtom,
  usePaywallStates,
} from '@/atoms/paywallAtoms';
import {
  useIsAgentCreditAllowanceGateEnabled,
  useIsHeidi2FullUpgradeFlowEnabled,
  useIsHeidi2OptInEnabled,
} from '@/features/flags';
import { useQueryCreditAllowance } from '@/hooks/query/useQueryCreditAllowance';
import useQueryUserTier from '@/hooks/query/useQueryUserTier';
import { useIsAppleCompliantBuild } from '@/hooks/useIsAppleCompliantBuild';
import useReverseTrial from '@/hooks/useReverseTrial';
import {
  resolveWorkBillingAnalyticsProps,
  trackWorkUpgradeActioned,
} from '@/models/analytics/features/work';
import {
  creditAllowanceRefusalAtom,
  creditAllowanceThresholdConsumedAtom,
  type CreditAllowanceThresholdSurface,
  type CreditUsagePopoverPlan,
  getCreditAllowanceThresholdBillingPlanKey,
  isCreditAllowanceThresholdBillingPlanResolved,
  isHeidi2OptInCreditUsageEnabled,
  isThresholdConsumedInRecord,
  markCreditAllowanceThresholdConsumedAtom,
  pricingPlansModalOpenAtom,
  relaxCreditAllowanceRefusalHold,
  resolveCreditUsagePopoverPlan,
  resolveCreditUsagePopoverTargetPlan,
  useIsMaxBillingSeat,
} from '@heidi/commerce';
import {
  getCreditAllowanceNudgeVariant,
  useQueryUserBillingInfo,
} from '@heidi/commerce/data';
import { type SubscriptionTier } from '@heidi/schema/ml-scribe';
import { getDefaultStore, useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import type { WorkSurface } from '@heidi/agents/contracts';

import {
  type AgentCreditAllowanceBannerSurface,
  resolveAgentCreditAllowanceBanner,
  shouldHideFollowUpQuestionsWhenCreditExhausted,
  shouldSkipCreditAllowanceSend,
} from './resolve-agent-credit-allowance-banner';

/** The banner owns its own dismissal — the side-nav popover closing must not hide it. */
const BANNER_SURFACE: CreditAllowanceThresholdSurface = 'banner';

/** The Max plan the banner sells: Practice Max to a Practice seat, Clinician Max otherwise. */
function trackQuotaUpgrade(
  action: 'viewed' | 'cta_clicked' | 'dismissed',
  analyticsSurface: WorkSurface,
  targetPlan: SubscriptionTier
): void {
  trackWorkUpgradeActioned({
    action,
    entry_point: 'quota',
    surface: analyticsSurface,
    target_plan: targetPlan,
    target_seat_type: 'max',
    ...resolveWorkBillingAnalyticsProps(),
  });
}

/**
 * Emits work_upgrade_actioned `viewed` once per shown upgrade banner state.
 * Call from the component that renders the banner, not from the state hook:
 * the hook also backs the composer chrome, so an effect there double-counts.
 */
export function useTrackQuotaUpgradeViewed(
  surface: AgentCreditAllowanceBannerSurface | null,
  analyticsSurface: WorkSurface,
  plan: CreditUsagePopoverPlan = 'clinicianMax'
): void {
  const targetPlan = resolveCreditUsagePopoverTargetPlan(plan);
  const viewedKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (surface == null || surface.kind !== 'upgrade') {
      viewedKeyRef.current = null;
      return;
    }
    // XXX: a non-dismissible banner is the forced-exhausted hold, a distinct view.
    const key = `${surface.threshold}:${surface.dismissible}`;
    if (viewedKeyRef.current === key) return;
    viewedKeyRef.current = key;
    trackQuotaUpgrade('viewed', analyticsSurface, targetPlan);
  }, [analyticsSurface, surface, targetPlan]);
}

export function useAgentCreditAllowanceBannerState(
  analyticsSurface: WorkSurface = 'project_chat'
) {
  const isHeidi2OptInEnabled = useIsHeidi2OptInEnabled();
  const isGateEnabled = useIsAgentCreditAllowanceGateEnabled();
  const isHeidi2FullUpgradeFlowEnabled = useIsHeidi2FullUpgradeFlowEnabled();
  const hideCTA = useIsAppleCompliantBuild();
  const refusal = useAtomValue(creditAllowanceRefusalAtom, {
    store: getDefaultStore(),
  });
  const forceExhausted = Boolean(refusal?.holdExhausted);
  const { data: billingInfo } = useQueryUserBillingInfo();
  const isOptInCreditUsageEnabled = isHeidi2OptInCreditUsageEnabled({
    isHeidi2OptInEnabled,
    heidiVersion: billingInfo?.heidi_version,
  });
  // Keep GET live after a 402 even when the opt-in cohort is off, so the
  // send-hold can relax without a reload once remaining recovers.
  const query = useQueryCreditAllowance(isOptInCreditUsageEnabled || forceExhausted);
  const allowance = query.data;
  const dataUpdatedAt = query.dataUpdatedAt ?? 0;
  // Overage behaviour keys on the seat: Practice Max gets the same
  // complimentary banner and send-through as Clinician Max.
  const isMaxSeat = useIsMaxBillingSeat();
  const { newUserTier } = useQueryUserTier();
  const { isClinicianPracticeTrialUser } = useReverseTrial();
  const plan = resolveCreditUsagePopoverPlan(newUserTier, isClinicianPracticeTrialUser);
  const targetPlan = resolveCreditUsagePopoverTargetPlan(plan);
  const consumedThresholds = useAtomValue(creditAllowanceThresholdConsumedAtom);
  const markThresholdConsumed = useSetAtom(markCreditAllowanceThresholdConsumedAtom);
  const { toggleClinicianMaxUpgradeModal } = usePaywallStates();
  const setEntryPoint = useSetAtom(clinicianMaxUpgradeModalEntryPointAtom);
  const setPricingPlansModalOpen = useSetAtom(pricingPlansModalOpenAtom);
  const threshold = getCreditAllowanceNudgeVariant(allowance);

  useEffect(() => {
    if (!refusal?.holdExhausted) return;
    if (!isGateEnabled) {
      relaxCreditAllowanceRefusalHold();
      return;
    }
    if (allowance == null) return;
    if (Number(allowance.remaining) <= 0) return;
    if (dataUpdatedAt < refusal.forcedAt) return;
    relaxCreditAllowanceRefusalHold();
  }, [allowance, dataUpdatedAt, isGateEnabled, refusal]);

  const billingPlanKey = isCreditAllowanceThresholdBillingPlanResolved(billingInfo)
    ? getCreditAllowanceThresholdBillingPlanKey(billingInfo)
    : null;

  const isConsumed =
    allowance != null &&
    threshold != null &&
    billingPlanKey != null &&
    isThresholdConsumedInRecord(
      consumedThresholds,
      billingPlanKey,
      allowance.cycle_end,
      threshold,
      BANNER_SURFACE
    );

  const surface = resolveAgentCreditAllowanceBanner({
    isOptInCreditUsageEnabled,
    isMaxSeat,
    threshold,
    isConsumed,
    forceExhausted,
  });

  const skipSendArgs = {
    isOptInCreditUsageEnabled,
    isGateEnabled,
    isMaxSeat,
    threshold,
    forceExhausted,
  };
  const hideFollowUpQuestions =
    shouldHideFollowUpQuestionsWhenCreditExhausted(skipSendArgs);
  const sendDisabled = shouldSkipCreditAllowanceSend(skipSendArgs);

  const handleDismiss = useCallback(() => {
    if (allowance == null || threshold == null || billingPlanKey == null) {
      return;
    }
    trackQuotaUpgrade('dismissed', analyticsSurface, targetPlan);
    markThresholdConsumed({
      billingPlanKey,
      cycleEnd: allowance.cycle_end,
      threshold,
      surface: BANNER_SURFACE,
    });
  }, [
    allowance,
    analyticsSurface,
    billingPlanKey,
    markThresholdConsumed,
    targetPlan,
    threshold,
  ]);

  const handleUpgradeClick = useCallback(() => {
    trackQuotaUpgrade('cta_clicked', analyticsSurface, targetPlan);
    // The pricing plans modal offers the viewer's valid upgrades (Practice Max
    // for a Practice seat); the single Clinician Max modal is the pre-flag path.
    if (isHeidi2FullUpgradeFlowEnabled) {
      setPricingPlansModalOpen(true);
      return;
    }
    setEntryPoint('agent_credit_allowance_banner');
    toggleClinicianMaxUpgradeModal(true);
  }, [
    analyticsSurface,
    isHeidi2FullUpgradeFlowEnabled,
    setEntryPoint,
    setPricingPlansModalOpen,
    targetPlan,
    toggleClinicianMaxUpgradeModal,
  ]);

  return useMemo(
    () => ({
      surface,
      plan,
      hideCTA,
      hideFollowUpQuestions,
      sendDisabled,
      cycleEnd: allowance?.cycle_end,
      onDismiss: handleDismiss,
      onUpgradeClick: handleUpgradeClick,
    }),
    [
      allowance?.cycle_end,
      handleDismiss,
      handleUpgradeClick,
      hideCTA,
      hideFollowUpQuestions,
      plan,
      sendDisabled,
      surface,
    ]
  );
}
